import React from 'react';
import { BarChart3, TrendingUp, Users, DollarSign, Target, Award } from 'lucide-react';
import { useDashboardMetrics } from '../../hooks/useDashboardMetrics';
import { StandardButton } from '../../components/standard';

interface CreditManagementManagerDashboardProps {
  onViewOperations?: () => void;
}

const CreditManagementManagerDashboard: React.FC<CreditManagementManagerDashboardProps> = ({ onViewOperations }) => {
  // Fetch real data from localStorage
  const { data: managerStats, isLoading } = useDashboardMetrics();

  if (isLoading || !managerStats) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4 mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard metrics...</p>
        </div>
      </div>
    );
  }

  const getPerformanceColor = (value: number, threshold: number) => {
    if (value >= threshold) return 'text-green-600';
    if (value >= threshold * 0.8) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Credit Management Dashboard</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">Strategic overview of credit repair, ID theft recovery, and credit development services</p>
            </div>
            <div className="flex items-center space-x-4">
              <StandardButton
                variant="primary"
                leftIcon={<Users className="h-4 w-4" />}
                onClick={onViewOperations}
              >
                View Operations
              </StandardButton>
            </div>
          </div>
        </div>

        {/* Key Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Clients</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{managerStats.totalClients}</p>
                <p className="text-sm text-green-600">+{managerStats.revenue.growth}% this month</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Score Increase</p>
                <p className="text-2xl font-bold text-green-600">+{managerStats.creditScoreImprovements.avgIncrease}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Points across all clients</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Monthly Revenue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">${(managerStats.revenue.monthly / 1000).toFixed(0)}K</p>
                <p className="text-sm text-green-600">+{managerStats.revenue.growth}% growth</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Award className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Client Satisfaction</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{managerStats.clientSatisfaction.overall}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">⭐ Out of 5</p>
              </div>
            </div>
          </div>
        </div>

        {/* Service Performance Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Credit Repair */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white mr-3">🔧</div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Credit Repair</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Active Clients</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{managerStats.servicePerformance.creditRepair.clients}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Avg Score Increase</span>
                <span className="font-semibold text-green-600">+{managerStats.servicePerformance.creditRepair.avgScoreIncrease}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Success Rate</span>
                <span className={`font-semibold ${getPerformanceColor(managerStats.servicePerformance.creditRepair.successRate, 85)}`}>
                  {managerStats.servicePerformance.creditRepair.successRate}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Avg Time</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{managerStats.servicePerformance.creditRepair.avgTime} months</span>
              </div>
            </div>
          </div>

          {/* ID Theft Recovery */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center text-white mr-3">🛡️</div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">ID Theft Recovery</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Active Cases</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{managerStats.servicePerformance.idTheft.clients}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Cases Resolved</span>
                <span className="font-semibold text-green-600">{managerStats.servicePerformance.idTheft.casesResolved}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Success Rate</span>
                <span className={`font-semibold ${getPerformanceColor(managerStats.servicePerformance.idTheft.successRate, 90)}`}>
                  {managerStats.servicePerformance.idTheft.successRate}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Avg Time</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{managerStats.servicePerformance.idTheft.avgTime} months</span>
              </div>
            </div>
          </div>

          {/* Credit Development */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center text-white mr-3">📈</div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Credit Development</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Active Clients</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{managerStats.servicePerformance.creditDevelopment.clients}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Avg Score Increase</span>
                <span className="font-semibold text-green-600">+{managerStats.servicePerformance.creditDevelopment.avgScoreIncrease}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Success Rate</span>
                <span className={`font-semibold ${getPerformanceColor(managerStats.servicePerformance.creditDevelopment.successRate, 85)}`}>
                  {managerStats.servicePerformance.creditDevelopment.successRate}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Avg Time</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{managerStats.servicePerformance.creditDevelopment.avgTime} months</span>
              </div>
            </div>
          </div>
        </div>

        {/* Credit Score Impact & Client Satisfaction */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Credit Score Impact */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <Target className="h-5 w-5 mr-2" />
              Credit Score Impact
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <span className="font-medium text-gray-900 dark:text-gray-100">50+ Point Increases</span>
                <span className="text-xl font-bold text-green-600">{managerStats.creditScoreImprovements.clients50Plus}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <span className="font-medium text-gray-900 dark:text-gray-100">100+ Point Increases</span>
                <span className="text-xl font-bold text-blue-600">{managerStats.creditScoreImprovements.clients100Plus}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <span className="font-medium text-gray-900 dark:text-gray-100">150+ Point Increases</span>
                <span className="text-xl font-bold text-purple-600">{managerStats.creditScoreImprovements.clients150Plus}</span>
              </div>
            </div>
          </div>

          {/* Client Satisfaction Breakdown */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Satisfaction Breakdown</h3>
            <div className="space-y-4">
              {Object.entries(managerStats.clientSatisfaction.breakdown).map(([category, rating]) => (
                <div key={category} className="flex items-center justify-between">
                  <span className="capitalize text-gray-600 dark:text-gray-400">{category}</span>
                  <div className="flex items-center">
                    <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-3">
                      <div
                        className="bg-yellow-500 h-2 rounded-full"
                        style={{ width: `${(rating / 5) * 100}%` }}
                      ></div>
                    </div>
                    <span className="font-bold text-gray-900 dark:text-gray-100 w-8">{rating}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Team Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Team Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-600">
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Team Member</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Clients</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Avg Score Increase</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Satisfaction</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {managerStats.teamPerformance.map((member, index) => (
                  <tr key={index} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-3 px-4 font-medium text-gray-900 dark:text-gray-100">{member.name}</td>
                    <td className="py-3 px-4 text-center text-gray-900 dark:text-gray-100">{member.clients}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        +{member.scoreIncrease}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center">
                        <span className="text-yellow-500 mr-1">⭐</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{member.satisfaction}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center font-medium text-gray-900 dark:text-gray-100">
                      ${member.revenue.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 6-Month Trends */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            6-Month Business Trends
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">New Clients</h4>
              <div className="space-y-2">
                {managerStats.monthlyTrends.newClients.map((value, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-12">Month {index + 1}</span>
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(value / 35) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 w-8">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Completed Cases</h4>
              <div className="space-y-2">
                {managerStats.monthlyTrends.completedCases.map((value, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-12">Month {index + 1}</span>
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${(value / 25) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 w-8">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Score Improvements</h4>
              <div className="space-y-2">
                {managerStats.monthlyTrends.scoreImprovements.map((value, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-12">Month {index + 1}</span>
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{ width: `${(value / 130) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 w-8">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Revenue (K)</h4>
              <div className="space-y-2">
                {managerStats.monthlyTrends.revenue.map((value, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-12">Month {index + 1}</span>
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${(value / 300) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 w-8">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditManagementManagerDashboard;